<template>

  <div v-if="editor" class="post-edit-container">
    <button class="save-post-button" @click="saveContent">
      SAVE
    </button>
    <div>
      <SeoEdit @form-updated="handleFormData" :formData="post"/>
    </div>
    <div class="control-group">
      <div class="button-group">
        <input type="file" ref="imageInput" @change="addImage" />
        <select v-model="selectedFont" id="font-selector" @change="changeFontFamily">
          <option v-for="font in fontFamilies" :key="font" :value="font">{{ font }}</option>
        </select>
        <select v-model="selectedFontSize" id="font-size-selector" @change="changeFontSize">
          <option v-for="fontSize in fontSizes" :key="fontSize" :value="fontSize">{{ fontSize }}</option>
        </select>
        <select v-model="selectedHeading" id="heading-selector" @change="changeHeading">
          <option v-for="heading in headingList" :key="heading.key" :value="heading.value">{{ heading.key }}</option>
        </select>
        <button @click="editor.chain().focus().toggleItalic().run()" :disabled="!editor.can().chain().focus().toggleItalic().run()" :class="{ 'is-active': editor.isActive('italic') }">
          Italic
        </button>
        <button @click="editor.chain().focus().toggleUnderline().run()" :disabled="!editor.can().chain().focus().toggleUnderline().run()" :class="{ 'is-active': editor.isActive('underline') }">
          Underline
        </button>
        <button @click="editor.chain().focus().toggleStrike().run()" :disabled="!editor.can().chain().focus().toggleStrike().run()" :class="{ 'is-active': editor.isActive('strike') }">
          Strike
        </button>
        <button @click="editor.chain().focus().setFontFamily('Inter').run()" :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'Inter' }) }">
          Inter
        </button>
        <input
            type="color"
            @input="editor.chain().focus().setColor($event.target.value).run()"
            :value="editor.getAttributes('textStyle').color"
        >
        <button @click="editor.chain().focus().setTextAlign('left').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }">
          Left
        </button>
        <button @click="editor.chain().focus().setTextAlign('center').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }">
          Center
        </button>
        <button @click="editor.chain().focus().setTextAlign('right').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }">
          Right
        </button>
        <button @click="editor.chain().focus().setTextAlign('justify').run()" :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }">
          Justify
        </button>
        <button @click="editor.chain().focus().unsetTextAlign().run()">
          Unset text align
        </button>
        <button @click="editor.chain().focus().toggleBulletList().run()" :class="{ 'is-active': editor.isActive('bulletList') }">
          Toggle bullet list
        </button>
        <button @click="editor.chain().focus().toggleOrderedList().run()" :class="{ 'is-active': editor.isActive('orderedList') }">
          Toggle ordered list
        </button>
        <button @click="editor.chain().focus().setSubscript().run()" :disabled="editor.isActive('subscript')">
          Set subscript
        </button>
        <button @click="editor.chain().focus().unsetSubscript().run()" :disabled="!editor.isActive('subscript')">
          Unset subscript
        </button>
        <button @click="setLink" :class="{ 'is-active': editor.isActive('link') }">
          Set link
        </button>
      </div>
    </div>
    <editor-content :editor="editor" class="main-editor"/>
  </div>
</template>

<script>
import Color from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import TextStyle from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import CustomImage from "../../../../components/editor/CustomImage.js"
import FontFamily from '@tiptap/extension-font-family'
import BulletList from '@tiptap/extension-bullet-list'
import FontSize from "tiptap-extension-font-size"
import TextAlign from '@tiptap/extension-text-align'
import Subscript from '@tiptap/extension-subscript'
import Heading from '@tiptap/extension-heading'
import Link from '@tiptap/extension-link'
import SeoEdit from "./SeoEdit.vue";
import { EditorContent, Editor } from '@tiptap/vue-2'
import {mapGetters} from "vuex";
import {Message} from "element-ui";

export default {
  components: {
    EditorContent,
    SeoEdit
  },

  data() {
    return {
      previousImages: [],
      editor: null,
      selectedFont: 'Arial',
      selectedFontSize: '10',
      selectedHeading: 'H1',
      content: '',
      headingList: [
        {
          key:'H1',
          value: 1
        },
         {
           key:'H2',
           value: 2
        },
         {
           key:'H3',
           value: 3
         },
         {
           key:'H4',
           value: 4
        },
      ],
      fontSizes: [
        '10',
        '12',
        '14',
        '16',
        '18',
      ],
      fontFamilies: [
        'Arial',
        'Times New Roman',
        'Georgia',
        'Verdana',
        'Courier New',
        'Tahoma',
        'Trebuchet MS',
        'Comic Sans MS',
      ],
      seoData: {},
      seoFormValidated: null
    }
  },
  mounted() {
    if (this.$route.params.id) {
      this.$store.dispatch('posts/getPost', this.$route.params.id)
    } else {
      this.initializeEditor('');
    }
  },
  computed: {
  ...mapGetters({
      post: 'posts/getPost',
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  watch: {
    post(newPost) {
      if (newPost && !this.editor) {
        this.initializeEditor(newPost.content);
        this.previousImages = this.getAllImages(this.editor.getHTML());
      }
    }
  },
  methods: {
    handleImageRemoval() {

      let currentImages = this.getAllImages(this.editor.getHTML());
      let removedImages = this.previousImages.filter(
          (img) => !currentImages.includes(img)
      );

      if(removedImages.length) {
        removedImages.forEach((image) => {
          this.$store.dispatch('media/deleteMedia', image.id);
        });
      }
      this.previousImages = currentImages;
    },
    getAllImages(html) {
      let tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      return Array.from(tempDiv.querySelectorAll('img')).map((img) => ({
        src: img.src,
        id: img.dataset.id,
      }));
    },
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('URL', previousUrl)
      if (url === null) {
        return
      }
      if (url === '') {
        this.editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .unsetLink()
            .run()

        return
      }
      this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: url })
          .run()
    },
    changeHeading() {
        this.editor.chain().focus().toggleHeading({ level: this.selectedHeading }).run();
    },
    changeFontSize() {
      this.editor.chain().focus().setFontSize(`${this.selectedFontSize}px`).run();
    },
    changeFontFamily() {
      this.editor.chain().focus().setFontFamily(this.selectedFont).run();
    },
    addImage(event) {
      let file = event.target.files[0];
      this.$store.dispatch('media/createMedia', {file: file, model_dir_name: 'posts'}).then(response => {
        this.editor.chain().focus().setImage({ src: response.url, 'data-id': response.id }).run();
      })
    },
    handleFormData(updatedFormData) {
      if(updatedFormData !== 'form not validated') {
        this.seoData = updatedFormData;
        this.seoFormValidated = true;
      } else {
        this.seoFormValidated = false;
      }
    },
    saveContent() {
      if(!this.seoFormValidated) {
        Message({
          message: 'SEO Form not validated',
          type: 'error',
          duration: 5 * 1000,
        })

        return false
      }
      const formData = new FormData();
      for (let key in this.seoData) {
        formData.append(key, this.seoData[key]);
      }
      formData.delete('seo_image')
      // if(!(formData.get('seo_image') instanceof File)) {
      //   formData.delete('seo_image')
      // }
      if(!(formData.get('image') instanceof File)) {
        formData.delete('image')
      }

      this.seoData.categories.map((item, index) => {
        if (typeof item === 'object' && item !== null && item.id) {
          formData.append(`category_ids[${index}]`, item.id);
        } else {
          formData.append(`category_ids[${index}]`, item);
        }
      });
      this.seoData.tags.map((item, index) => {
        if (typeof item === 'object' && item !== null && item.id) {
          formData.append(`tag_ids[${index}]`, item.id);
        } else {
          formData.append(`tag_ids[${index}]`, item);
        }
      });
      formData.append('author_id', this.seoData.author.id)
      formData.append('content', this.editor.getHTML())

      if (this.$route.params.id){
        this.$store.dispatch('posts/updatePost', {'formData': formData,'postId' : this.$route.params.id})
      } else {
        this.$store.dispatch('posts/createPost', {'formData': formData})
      }

    },
    initializeEditor(content) {
      this.editor = new Editor({
        extensions: [
          Color.configure({ types: [TextStyle.name, ListItem.name] }),
          TextStyle.configure({ types: [ListItem.name] }),
          StarterKit,
          CustomImage,
          Underline,
          FontFamily,
          FontSize,
          Color,
          TextAlign.configure({
            types: ['heading', 'paragraph'],
          }),
          BulletList,
          Subscript,
          Heading,
          Link.configure({
            openOnClick: true,
            defaultProtocol: 'https',
          }),
        ],
        content: content,
        //onUpdate: this.handleImageRemoval.bind(this)
      })
    },
    handleNewBlogImage(image) {
      this.$store.dispatch('media/createMedia', {file: image, model_dir_name: 'posts'}).then(response => {
        this.editor.chain().focus().setImage({ src: response.url, 'data-id': response.id }).run();
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../../../assets/scss/editor';
</style>